import { Component, OnInit } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { CommonModule, KeyValue } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { SystemConnectionDetailsDialog } from './system-connection-details.dialog';
import { SystemConnectionKeyDialog } from './system-connection-key.dialog';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CustomPaginatorComponent, CustomContentLoaderComponent, PortalHotToastService, MessageBoxComponent, formControlErrorKeys, formControlErrorMessage  } from '@portal-workspace/grow-ui-library';
import { SystemConnections, toCapitalCaseFirstChar, toLabelCase, ConnectionDetails, SystemStatusInput, ServiceStatus } from '@portal-workspace/grow-shared-library';
import { AdminService } from '../../service/admin.service';
import { animate, state, style, transition, trigger } from '@angular/animations';
import * as CryptoJS from 'crypto-js';
import { createAsyncStore } from '@ngneat/loadoff';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

interface SystemConnection {
  name: string;
  status: boolean;
  message: string;
  details?: ConnectionDetails;
  detailRow?: boolean;
  formGroup?: FormGroup;
}

interface SystemStatusDetailsResponse {
  salt: string;
  details: ConnectionDetails;
}
interface SystemStatusCheckListParams extends Partial<SystemStatusInput> {
  details?: boolean;
  service?: string;
}

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  templateUrl: './system-connections.page.html',
  styleUrls: ['./system-connections.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    FlexLayoutModule,
    MatTableModule,
    MatTooltipModule,
    MatProgressBarModule,
    CustomPaginatorComponent,
    CustomContentLoaderComponent,
    MessageBoxComponent,
  ],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ]
})
export class SystemConnectionsPage implements OnInit {
  errorKeys = formControlErrorKeys;
  formControlErrorMessage = formControlErrorMessage;

  subscriptions: Subscription[] = [];
  loader = {initial: true, testAll: false, decrypting: false};
  testingConnectionNames: Set<string> = new Set();
  systemConnections: SystemConnection[] = [];
  encryptedConnections: SystemConnection[] = [];
  displayColumns = ['icon','name', 'status', 'actions'];
  expandedElement: SystemConnection | null = null;
  isDecrypted: boolean = false;
  salt: string = '';
  isProgressBarVisible: boolean = false;
  showPasswordMap: { [key: string]: boolean } = {};
  shimmerPlaceholder: SystemConnection[] = [{
    name: '',
    status: false,
    message: '',
    detailRow: false
  }];
  store = createAsyncStore();
  errorTitle = 'Error Occurred!';
  errorMessage = 'Please try again.';
  loadingDetails = false;
  isEditing: boolean = false;

  formGroups: { [connectionName: string]: FormGroup } = {};
  
  private initializePasswordVisibility(connection: SystemConnection) {
    if (connection.details) {
      Object.entries(connection.details).forEach(([key, detail]) => {
        if (this.isPasswordType(detail)) {
          this.showPasswordMap[key] = false;
        }
      });
    }
  }

  private updatePasswordVisibilityForConnections(connections: SystemConnection[]) {
    // Only initialize for the expanded connection to improve performance
    const baseConnection = connections.find(conn => !conn.detailRow && conn.name === this.expandedElement?.name);
    if (baseConnection) {
      this.initializePasswordVisibility(baseConnection);
    }
  }

  get testAllDisabled(): boolean {
    return this.loader.initial || 
           this.loader.testAll || 
           this.testingConnectionNames.size > 0 ||
           this.systemConnections.some(conn => this.isConnectionTesting(conn.name));
  }

  isBaseRow = (_: number, connection: SystemConnection) => !connection.detailRow;
  isDetailRow = (_: number, connection: SystemConnection) => connection.detailRow;

  constructor(
    private dialog: MatDialog,
    private adminService: AdminService,
    private toastService: PortalHotToastService,
  ) { }

  ngOnInit(): void {
    this.loadSystemConnections();
  }

  public loadSystemConnections(all: boolean = false): void {
    this.loader.initial = true;
    this.loader.testAll = true;
    this.isProgressBarVisible = true;
    this.isDecrypted = false;
    this.isEditing = false;
    this.store = createAsyncStore();
    // Reset password visibility map when reloading connections
    this.showPasswordMap = {};
    this.expandedElement = null;
    const subscription = this.adminService.getSystemStatusCheckList({
      all: true // This will check all systems
    }).pipe(
      this.store.track(),
      this.toastService.topMenuLoadingObservable(),
      this.toastService.publishErrorNotificationObservable({
        errorTitle: this.errorTitle,
        errorMessage: this.errorMessage,
        retryFn: () => this.loadSystemConnections(all)
      })
    ).subscribe({
      next: (response) => {
        if (response.payload) {
          // Transform API response into SystemConnection format
          // Create the data source with both base and detail rows
          const baseConnections = Object.entries(response.payload).map(([key, value]) => {
            const name = toLabelCase(toCapitalCaseFirstChar( SystemConnections[key as keyof typeof SystemConnections]))
            return {
              name: name,
              status: (value as ServiceStatus).status ?? false,
              message: (value as ServiceStatus).message ?? '',
              details: (value as ServiceStatus).details ?? {},
              detailRow: false
            }
          });

          // Create the data source with both base and detail rows
          this.systemConnections = baseConnections.reduce((acc: SystemConnection[], connection) => {
            acc.push(connection); // Add the base row
            acc.push({ ...connection, detailRow: true }); // Add the detail row
            return acc;
          }, []);

          // Initialize password visibility states for all connections
          this.updatePasswordVisibilityForConnections(this.systemConnections);
          this.encryptedConnections = structuredClone(this.systemConnections);
          this.loader.initial = false;
          this.loader.testAll = false;
          this.isProgressBarVisible = false;
        }
      },
      error: (error: any) => {
        console.error('Error loading system connections:', error);
        this.loader.initial = false;
        this.loader.testAll = false;
        this.isProgressBarVisible = false;
      }
    });
    this.subscriptions.push(subscription);
  }

  async testConnection(connection: SystemConnection): Promise<void> {
    this.isDecrypted = false;
    this.showPasswordMap = {};
    this.testingConnectionNames.add(connection.name);
    this.isProgressBarVisible = true;
    this.isEditing = false;
    this.store = createAsyncStore();
    
    let key = Object.entries(SystemConnections).find(
      ([_, value]) => value === connection.name
    )?.[0];
    
    const subscription = this.adminService.getSystemStatusCheckList({ [key as keyof SystemStatusInput]: true }).pipe(
      this.store.track(),
      this.toastService.topMenuLoadingObservable(),
      this.toastService.publishErrorNotificationObservable({
        errorTitle: this.errorTitle,
        errorMessage: this.errorMessage,
        retryFn: () => this.testConnection(connection)
      })
    ).subscribe({
      next: (response) => {
        if (response.payload) {
          // Find the tested system in the response
          const testedSystem = Object.entries(response.payload).find(([key]) => {
            return SystemConnections[key as keyof typeof SystemConnections] === connection.name
          });

          if (testedSystem) {
            // Update only the tested connection in the systemConnections array
            const [key, status] = testedSystem;
            if (typeof status !== 'string' && status) {
              const details = status.details ? Object.entries(status.details).reduce((acc, [detailKey, detail]) => {
                if (!acc) acc = {};
                // Handle the nested value structure
                const detailObj = detail as { [value: string]: string | number | boolean | object; type: 'text' | 'password' | 'number' | 'date' | 'email' | 'boolean' };
                const value = Object.entries(detailObj).find(([k]) => k !== 'type')?.[1];
                acc[detailKey] = {
                  value: value || '',
                  type: detailObj.type
                };
                return acc;
              }, {} as NonNullable<SystemConnection['details']>) : undefined;

              // Find and update the specific connection in the array
              const index = this.systemConnections.findIndex(conn => conn.name === connection.name);
              if (index !== -1) {
                // Update status and message
                this.systemConnections[index].status = Boolean(status.status);
                this.systemConnections[index].message = status.message?.toString() || '';
                this.systemConnections[index + 1].status = Boolean(status.status);
                this.systemConnections[index + 1].message = status.message?.toString() || '';

                // Update details if available
                if (details) {
                  this.systemConnections[index].details = details;
                  this.systemConnections[index + 1].details = details;
                }

                // Update encrypted connections as well
                const encIndex = this.encryptedConnections.findIndex(conn => conn.name === connection.name);
                if (encIndex !== -1) {
                  this.encryptedConnections[encIndex].status = Boolean(status.status);
                  this.encryptedConnections[encIndex].message = status.message?.toString() || '';
                  this.encryptedConnections[encIndex + 1].status = Boolean(status.status);
                  this.encryptedConnections[encIndex + 1].message = status.message?.toString() || '';
                  if (details) {
                    this.encryptedConnections[encIndex].details = details;
                    this.encryptedConnections[encIndex + 1].details = details;
                  }
                }
              }
            }
          }
        }
        this.testingConnectionNames.delete(connection.name);
        this.isProgressBarVisible = false;
      },
      error: (error: any) => {
        console.error('Error testing connection:', error);
        this.testingConnectionNames.delete(connection.name);
        this.isProgressBarVisible = false;
      }
    });
    this.toastService.snackBarObservable(`Testing ${connection.name} connection...`);
    this.subscriptions.push(subscription);
  }

  isConnectionTesting(connectionName: string): boolean {
    return this.testingConnectionNames.has(connectionName);
  }

  showDetails(connection: SystemConnection): void {
    this.dialog.open(SystemConnectionDetailsDialog, {
      data: {
        name: connection.name,
        details: connection.details
      }
    });
  }

  async onRowClick(connection: SystemConnection): Promise<void> {
    this.loadingDetails = true;
    this.isDecrypted = false;
    this.isEditing = false;
    if (this.expandedElement?.name === connection.name) {
      this.expandedElement = null;
      // Clear password visibility states for collapsed connection
      if (connection.details) {
        Object.keys(connection.details).forEach(key => {
          delete this.showPasswordMap[key];
        });
      }
    } else {
      // Clear previous connection's password visibility states
      if (this.expandedElement?.details) {
        Object.keys(this.expandedElement.details).forEach(key => {
          delete this.showPasswordMap[key];
        });
      }
      this.expandedElement = connection;
      // Initialize password visibility states for new expanded connection
      this.initializePasswordVisibility(connection);

      // Find the service key for this connection
      const serviceKey = Object.entries(SystemConnections).find(
        ([_, value]) => value === connection.name
      )?.[0];

      if (serviceKey) {
        try {
          const subscription = this.adminService.getSystemStatusCheckList({
            details: true,
            service: serviceKey
          } as SystemStatusCheckListParams).pipe(
            this.store.track(),
            this.toastService.topMenuLoadingObservable(),
            this.toastService.publishErrorNotificationObservable({
              errorTitle: this.errorTitle,
              errorMessage: this.errorMessage,
              retryFn: () => this.onRowClick(connection)
            })
          ).subscribe({
            next: (response) => {
              if (response.payload) {
                const payload = response.payload as unknown as SystemStatusDetailsResponse;
                this.salt = response.payload.salt?.toString() || '';
                // Update connection status and message
                const index = this.systemConnections.findIndex(conn => conn.name === connection.name);
                if (index !== -1) {
                  this.systemConnections[index].status = Boolean(response.payload.status ?? connection.status);
                  this.systemConnections[index].message = (response.payload.message ?? connection.message)?.toString() || '';
                  this.systemConnections[index + 1].status = Boolean(response.payload.status ?? connection.status);
                  this.systemConnections[index + 1].message = (response.payload.message ?? connection.message)?.toString() || '';
                }
                
                if (payload.details) {
                  // Transform the details with proper key formatting
                  const transformedDetails = transformDetailsToLabels(payload.details);
                  // Update the connection details in both arrays
                  if (index !== -1) {
                    this.systemConnections[index].details = transformedDetails;
                    this.systemConnections[index + 1].details = transformedDetails;
                    
                    // Update encrypted connections as well
                    const encIndex = this.encryptedConnections.findIndex(conn => conn.name === connection.name);
                    if (encIndex !== -1) {
                      this.encryptedConnections[encIndex].details = transformedDetails;
                      this.encryptedConnections[encIndex + 1].details = transformedDetails;
                    }
                  }
                }
              }
              this.loadingDetails = false;
            },
            error: (error) => {
              console.error('Error fetching connection details:', error);
              this.loadingDetails = false;
              this.expandedElement = null;
            }
          });
          this.subscriptions.push(subscription);
        } catch (error) {
          console.error('Error in onRowClick:', error);
          this.loadingDetails = false;
          this.expandedElement = null;
        }
      }
    }
  }

  private decryptValue(encryptedValue: string, key: string): string {
    try {
      if (!encryptedValue) return encryptedValue;

      let valueToDecrypt = encryptedValue;
      try {
        const parsed = JSON.parse(encryptedValue);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          if (!('iv' in parsed && 'encryptedData' in parsed)) {
            const decryptedObj: { [key: string]: any } = {};
            for (const [objKey, objValue] of Object.entries(parsed)) {
              decryptedObj[objKey] = this.decryptValue(
                typeof objValue === 'string' ? objValue : JSON.stringify(objValue), 
                key
              );
            }
            valueToDecrypt = JSON.stringify(decryptedObj);
          }
        }
      } catch {
        throw new Error('An error occurred while decrypting the details.');
      }

      // Generate encryption key using PBKDF2 (same as backend)
      const keySize = 256 / 32;
      const iterations = 10000;
      const derivedKey = CryptoJS.PBKDF2(key, this.salt, {
        keySize: keySize,
        iterations: iterations
      });
     
      // Parse the encrypted data structure
      const { iv, encryptedData } = JSON.parse(valueToDecrypt);
      if(!iv || !encryptedData){
        throw new Error('Incorrect secret key. Please check your key and try again.');
      }
      // Decrypt using the same parameters as backend
      const decrypted = CryptoJS.AES.decrypt(encryptedData, derivedKey, {
        iv: CryptoJS.enc.Hex.parse(iv),
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });

      const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);

      if (!decryptedStr) {
        throw new Error('Incorrect secret key. Please check your key and try again.');
      }

      // Try parsing the decrypted value as it might be a stringified JSON
      try {
        this.isDecrypted = true;
        return JSON.parse(decryptedStr);
      } catch {
        this.isDecrypted = false;
        return '********';
      }
    } catch (error) {
      this.isDecrypted = false;
      throw error;
    }
  }
  private decryptNestedFields = (details: any, secret: string): any => {
    if (!details || typeof details !== 'object') return details;
    const result: any = {};
    for (const [key, field] of Object.entries(details)) {
      if (field && typeof field === 'object' && 'value' in field && 'type' in field) {
        const decryptedValue = field.value;
  
        try {
          // If parsed value is another encrypted-like object, recurse
          if (
            decryptedValue &&
            typeof decryptedValue === 'object' &&
            Object.values(decryptedValue).every(
              (v: any) => v && typeof v === 'object' && 'value' in v && 'type' in v
            )
          ) {
            result[key] = {
              value: this.decryptNestedFields(decryptedValue, secret),
              type: field.type
            };
          } else {
          const parsed = JSON.parse(decryptedValue as string);
            result[key] = {
              value:  this.decryptValue(JSON.stringify(parsed), secret),
              type: field.type
            };
          }
        } catch {
          // Not JSON, treat as string
          try {
            result[key] = {
              value: this.decryptValue(JSON.stringify(field.value), secret),
              type: field.type
            };
          } catch (decryptError) {
            throw new Error('Incorrect secret key. Please check your key and try again.');
          }
        }
      } else {
        // Recurse into any unexpected nested object
        result[key] = this.decryptNestedFields(field, secret);
      }
    }
    return result;
  }

  async handleDecryption(connection: SystemConnection): Promise<void> {
    if (this.isDecrypted) {
      this.isDecrypted = false;
      const index = this.systemConnections.findIndex(conn => conn.name === connection.name);
      if (index !== -1) {
        const originalConnection = this.encryptedConnections.find(conn => conn.name === connection.name);
        if (originalConnection) {
          this.systemConnections[index].details = { ...originalConnection.details };
          this.systemConnections[index + 1].details = { ...originalConnection.details };
        }
      }
      return;
    }

    // Open key dialog
    const dialogRef = this.dialog.open(SystemConnectionKeyDialog, {
      width: '400px',
      disableClose: false
    });

    try {
      const key = await dialogRef.afterClosed().toPromise();
      if (!key) {
        return;
      }
      this.loader.decrypting = true;
      // Find the original encrypted values
      const originalConnection = this.encryptedConnections.find(conn => conn.name === connection.name);
      
      // Decrypt each value in the details object
      let decryptedDetails: ConnectionDetails = {};

      if (originalConnection?.details) {
        try {
          decryptedDetails = this.decryptNestedFields(originalConnection.details, key);
        } catch(error:any) {
          throw error;
        }
      }
      this.loader.decrypting = false;

      // Update the connection with decrypted details
      const index = this.systemConnections.findIndex(conn => conn.name === connection.name);
      if (index !== -1) {
        this.systemConnections[index].details = decryptedDetails;
        this.systemConnections[index + 1].details = decryptedDetails;
      }
      this.isDecrypted = true;
    } catch (error: any) {
      this.isDecrypted = false;
      this.loader.decrypting = false;
      this.toastService.error(error.message);
    }
  }

  getDetailType(detail: any): string {
    if (detail && typeof detail === 'object' && 'type' in detail) {
      const type = (detail as { type: string }).type;
      switch (type) {
        case 'password':
          return 'password';
        case 'number':
          return 'number';
        case 'date':
          return 'date';
        case 'email':
          return 'email';
        default:
          return 'text';
      }
    }
    return 'text';
  }
  isGroupedArrayObject(detail: any): boolean {
    return detail && typeof detail === 'object' && 'type' in detail && detail.type === 'array-object-grouped';
  }
  getDetailValue(detail: any): string | { key: string; value: any }[] {
    if (
      detail &&
      typeof detail === 'object' &&
      'value' in detail &&
      detail.value !== null
    ) {
      const value = detail.value;
      if (
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        if(this.isGroupedArrayObject(value)){
          return value;
        }
        const allEntriesHaveValue = Object.values(value).every(
          (entry: any) => typeof entry === 'object' && entry !== null && 'value' in entry
        );
  
        if (allEntriesHaveValue) {
          return Object.entries(value).map(([key, val]: [string, any]) => ({
            key,
            value: val.value
          }));
        }
      }
      return value;
    }
    return '';
  }
  isKeyValueArray(val: any): val is { key: string; value: any }[] {
    return Array.isArray(val) && val.every(item => 'key' in item && 'value' in item);
  }
  getKeyValueArray(detail: any): { key: string; value: any }[] | null {
    const result = this.getDetailValue(detail);
    return this.isKeyValueArray(result) ? result : null;
  }
  isPasswordType(detail: any): boolean {
    return this.getDetailType(detail) === 'password';
  }

  retry() {
    this.expandedElement = null;
    this.showPasswordMap = {};
    this.isDecrypted = false;
    this.loadSystemConnections();
  }

  getDetailKeyString(detail: KeyValue<string, ConnectionDetails[string]>): string {
    return detail.key;
  }

  togglePasswordVisibility(detail: any, event: Event): void {
    event.stopPropagation();
    const key = this.getDetailKeyString(detail);
    // Toggle visibility state
    this.showPasswordMap[key] = !this.showPasswordMap[key];
  }

  isPasswordVisible(detail: any): boolean {
    const key = this.getDetailKeyString(detail);
    return this.showPasswordMap[key] || false;
  }

  private createFormControlFromValue(value: any, type: string): FormControl {
    let validators = [Validators.required];
    let initialValue = value || '';
    
    switch(type) {
      case 'email':
        validators.push(Validators.email);
        break;
      case 'number':
        validators.push(Validators.pattern(/^-?\d*\.?\d+$/));
        initialValue = value || 0;
        break;
      case 'date':
        validators.push(Validators.pattern(/^\d{4}-\d{2}-\d{2}$/));
        break;
      case 'boolean':
        initialValue = Boolean(value);
        break;
    }
    
    return new FormControl(initialValue, validators);
  }

  private createNestedFormGroup(details: any): FormGroup {
    const group: { [key: string]: FormControl | FormGroup } = {};
    
    Object.entries(details).forEach(([key, detail]: [string, any]) => {
      if (detail && typeof detail === 'object') {
        if ('value' in detail && 'type' in detail) {
          if (typeof detail.value === 'object' && !Array.isArray(detail.value)) {
            group[key] = this.createNestedFormGroup(detail.value);
          } else {
            group[key] = this.createFormControlFromValue(detail.value, detail.type);
          }
        } else {
          group[key] = this.createNestedFormGroup(detail);
        }
      }
    });
    
    return new FormGroup(group);
  }

  createFormGroupFromDetail(detail: any, connectionName: string) {
    if (!detail) return;
    
    const formGroup = this.createNestedFormGroup(detail);
    this.formGroups[connectionName] = formGroup;
  }

  getFormControl(connectionName: string, path: string|any): FormControl | null {
    const group = this.formGroups[connectionName];
    if (!group) return null;

    try {
      const control = path.split('.').reduce((acc: any, part: any | string) => acc?.get(part), group);
      return control instanceof FormControl ? control : null;
    } catch {
      return null;
    }
  }

  getFormControlError(control: FormControl | null): string {
    if (!control || !control.errors || !control.touched) return '';

    const error = Object.keys(control.errors)[0];
    switch (error) {
      case 'required':
        return 'This field is required';
      case 'email':
        return 'Please enter a valid email address';
      case 'pattern':
        return 'Please enter a valid value';
      default:
        return 'Invalid value';
    }
  }

  handleEdit(connection: SystemConnection): void {
    if (this.isEditing) {
      this.isEditing = false;
      return;
    }
    
    this.isEditing = true;
    if (connection.details) {
      this.createFormGroupFromDetail(connection.details, connection.name);
    }
  }

  saveConnectionDetails(connection: SystemConnection): void {
    const formGroup = this.formGroups[connection.name];
    if (!formGroup) return;

    if (formGroup.invalid) {
      formGroup.markAllAsTouched();
      return;
    }

    // TODO: Implement API call to save connection details
    this.isEditing = false;
  }

  cancelEdit(): void {
    this.isEditing = false;
  }

} 

function transformDetailsToLabels(input: any): ConnectionDetails {
  const formatted: ConnectionDetails = {};
  for (const [key, item] of Object.entries(input)) {
    const labelKey = toLabelCase(key);

    if (
      item &&
      typeof item === 'object' &&
      'value' in item &&
      'type' in item
    ) {
      if (
        typeof item.value === 'object' &&
        item.value !== null &&
        !Array.isArray(item.value)
      ) {
        // Recursively format nested object
        formatted[labelKey] = {
          value: transformDetailsToLabels(item.value),
          type: 'object' as ('number' | 'boolean' | 'text' | 'password' | 'date' | 'email')
        };
      } else {
        // Direct value field
        formatted[labelKey] = {
          value: String(item.value), // Convert to string to ensure type safety
          type: item.type as ('number' | 'boolean' | 'text' | 'password' | 'date' | 'email')
        };
      }
    }
  }

  return formatted;
}